import { useMemo } from 'react';
import { useGetSMSConversationMessages } from 'services/hooks/queries';
import { Trash2, Download } from 'lucide-react';
import { Modal, Button, MessageList } from 'common/components';
import type { SMSDataResponse } from 'services/api/getSMSData';
import {
    formatLeadStatus,
    formatSMSStatus,
    formatSMSDisposition,
    getLeadStatusStyle,
    getSMSStatusStyle,
} from '../../utils';
import styles from './SMSDetailsModal.module.scss';
import type { SMSLead } from '../SMSTable';

export interface SMSDetailsModalProps {
    /** When set, the modal is open and conversation messages are loaded for this SMS. */
    sms: SMSLead | null;
    onClose: () => void;
    onDelete: (params: { selectedCallId: string }) => void;
    onExportCSV: (params: { selectedCallId?: string }) => void;
}

const mapConversationMessages = (messages: SMSDataResponse | undefined): SMSLead[] => {
    if (!messages?.data?.length) return [];

    return messages.data.map((sms) => {
        const smsDateTime = new Date(sms.sentTime || sms.scheduledDate);
        return {
            id: sms.crmId,
            smsId: sms.smsId,
            conversationId: sms.conversationId || sms.smsId,
            smsDate: smsDateTime.toLocaleDateString('en-US'),
            smsTime: smsDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            status: sms.status,
            crmStatus: sms.partyStage || 'NEW_LEAD',
            crmId: sms.crmId,
            name: sms.contactName || 'Unknown',
            number: sms.phoneNumber || 'Unknown',
            messageContent: sms.messageContent || '',
            messageDirection: sms.messageDirection,
            disposition: sms.disposition,
            deliveredTime: sms.deliveredTime,
        };
    });
};

export const SMSDetailsModal: React.FC<SMSDetailsModalProps> = ({ sms, onClose, onDelete, onExportCSV }) => {
    const isOpen = Boolean(sms);
    const { smsConversationMessages, isSMSConversationMessagesFetching } = useGetSMSConversationMessages(
        sms?.conversationId,
        isOpen
    );

    const transcriptMessages = useMemo(
        () => mapConversationMessages(smsConversationMessages),
        [smsConversationMessages]
    );

    if (!isOpen || !sms) return null;

    const messageItems = transcriptMessages.map((message) => ({
        smsTime: message.smsTime,
        smsDate: message.smsDate,
        speakerLabel: message.messageDirection === 'INBOUND' ? 'Customer' : 'Contact',
        text: message.messageContent || '(No message content)',
        direction: (message.messageDirection === 'INBOUND' ? 'inbound' : 'outbound') as 'inbound' | 'outbound',
        status: message.status,
    }));

    const footer = (
        <div className='flex justify-end gap-md'>
            <Button variant='outline-secondary' onClick={onClose}>
                Cancel
            </Button>
            <Button
                variant='primary'
                icon={Trash2}
                onClick={() => onDelete({ selectedCallId: sms.smsId })}
                disabled={isSMSConversationMessagesFetching}
            >
                Delete SMS
            </Button>
            <Button
                variant='primary'
                icon={Download}
                onClick={() => onExportCSV({ selectedCallId: sms.smsId })}
                disabled={isSMSConversationMessagesFetching}
            >
                Export CSV
            </Button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='md'
            showDefaultFooter={false}
            footer={footer}
            isLoadingOverlay={isSMSConversationMessagesFetching}
            title={<h2>SMS Conversation Transcript</h2>}
        >
            <div className={styles.callDetailsContainer}>
                <div className={styles.group}>
                    <div className={styles.title}>SMS Date</div>
                    <div className={styles.value}>
                        {sms.smsDate} {sms.smsTime}
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>SMS Status</div>
                    <div>
                        <span style={getSMSStatusStyle(sms.status)}>{formatSMSStatus(sms.status)}</span>
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Party Status</div>
                    <div className={styles.value}>
                        <span style={getLeadStatusStyle(sms.crmStatus)}>{formatLeadStatus(sms.crmStatus)}</span>
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>SMS Disposition</div>
                    <div className={styles.value}>{sms.disposition ? formatSMSDisposition(sms.disposition) : '-'}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Message Direction</div>
                    <div className={styles.value}>
                        {sms.messageDirection ? formatSMSStatus(sms.messageDirection) : '-'}
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>CRM ID</div>
                    <div className={styles.value}>{sms.crmId || '-'}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Name</div>
                    <div className={styles.value}>{sms.name}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Number</div>
                    <div className={styles.value}>{sms.number}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>SMS ID</div>
                    <div className={styles.value}>{sms.smsId || '-'}</div>
                </div>
            </div>

            <div className={styles.callDetailsContainer}>
                <div className={styles.group} style={{ gridColumn: '1 / -1' }}>
                    <div className={styles.messagesSectionTitle}>Message</div>
                    <MessageList items={messageItems} emptyMessage='No message content' />
                </div>
            </div>
        </Modal>
    );
};
